import { Component, lazy, Suspense } from 'react';
import { getAccessToken } from '../../services/auth.js';
import { pushDiagnostic } from '../../utils/diagnosticsBuffer.js';

// Lazy so a crash in the boundary's own fallback can't be caused by the
// report dialog's import graph, and so the happy path never loads it.
const IssueReportModal = lazy(() => import('../IssueReport/IssueReportModal.jsx'));

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: '', reporting: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    pushDiagnostic('render-crash', error, { stack: String(error?.stack || '').slice(0, 600) });
    this.setState({ componentStack: info?.componentStack || '' });
  }

  render() {
    if (this.state.error) {
      const { error, componentStack, reporting } = this.state;
      // The report endpoint needs a login; on a crash before/without one the
      // button would only bounce the user through a token refresh.
      const canReport = !!getAccessToken();
      const prefill = {
        category: 'bug',
        title: `App crashed: ${String(error?.message || error).slice(0, 150)}`,
        body: `The app showed "Something went wrong".\n\nWhat I was doing:\n\n\n---\n${String(error?.stack || error?.message || error).slice(0, 3000)}\n${componentStack.slice(0, 2000)}`,
      };
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100vh',
          background: '#0f1117', color: '#e5e7eb', fontFamily: 'sans-serif',
          padding: '2rem', textAlign: 'center',
        }}>
          <h2 style={{ color: '#f87171', marginBottom: '0.75rem' }}>Something went wrong</h2>
          <p style={{ color: '#9ca3af', marginBottom: '1.5rem', maxWidth: 480 }}>
            The app encountered an unexpected error. Try reloading the page. If the problem
            persists, please {canReport ? 'send us a report' : 'contact support'}.
          </p>
          <pre style={{
            background: '#1f2937', color: '#fbbf24', padding: '1rem',
            borderRadius: 6, fontSize: '0.75rem', maxWidth: '90vw',
            overflowX: 'auto', textAlign: 'left', marginBottom: '1.5rem',
          }}>
            {error.message}
          </pre>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1.5rem', background: '#3b82f6', color: '#fff',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              Reload
            </button>
            {canReport && (
              <button
                onClick={() => this.setState({ reporting: true })}
                style={{
                  padding: '0.5rem 1.5rem', background: 'transparent', color: '#e5e7eb',
                  border: '1px solid #4b5563', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem',
                }}
              >
                Report this problem
              </button>
            )}
          </div>
          {reporting && (
            <Suspense fallback={null}>
              <IssueReportModal
                onClose={() => this.setState({ reporting: false })}
                prefill={prefill}
                pageContext={{ view: 'crash', href: window.location.href }}
              />
            </Suspense>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
